import { Storage } from 'aws-amplify';

import { type CreateFlightExpenseInput } from '../../../API';
import { logError } from '../../../utils/ErrorLog/ErrorLog';
import { type FlightDocumentType } from '../../FlightDocuments/FlightDocument';
import {
    deleteUnheldExpenseContents,
    getLocalDocumentContentRow,
    markLocalDocumentContentUploaded,
} from '../../FlightDocuments/LocalDocumentContent';
import {
    type FlightExpenseContent,
    type FlightExpenseRecord,
    isBankLinked,
    MEDIA_FILES_NOT_CREATED,
} from '../../FlightExpenses/FlightExpense';
import { type FlightLegPhase } from '../../FlightLegs/FlightLeg';
import { getDb } from '../LocalDB';
import { getStandardItemHandler } from '../standardItemHandler';
import { type DbSyncerUpDefinition } from '../syncHelpers';
import { getStandardSyncerFields } from '../syncHelpers';
import { type DbCreated } from '../types';

let counter = 0;

export const flightExpenseItemFields = `
    id
    deleted
    editableByUserIds
    createdAt
    aircraftId
    flightId
    legId
    phase
    bankTransactionId
    contentKeys { key type }
    mediaFilesNotCreated
    title
    caption
    documentType
    expenseMonth
    status
    name
    amount
    currency
    paymentMethod
    paymentProvider
    reason
    reasonNote
    includes
    noDocumentReason
    noDocumentNote
    _version
`.trim();

// Two expenses that share a file can sync in one batch; the second waits for the first upload.
const uploadsInFlight = new Map<string, Promise<void>>();

async function uploadPendingContent(entry: FlightExpenseContent): Promise<void> {
    try {
        const local = await getLocalDocumentContentRow(entry.key);
        // No local blob: the entry came from the server. A marked blob already went up, with this
        // expense or with another one that holds the same file.
        if (!local || local.uploaded) {
            return;
        }
        const expires = new Date();
        expires.setFullYear(expires.getFullYear() + 1);
        await Storage.put(entry.key, local.content, {
            contentType: entry.type,
            expires,
        });
        // Marked before the mutation is sent, so a lost response cannot make the next sync upload
        // the file again.
        await markLocalDocumentContentUploaded(entry.key);
    } catch (e: any) {
        logError(e.message, 'flightExpenses/upload');
        throw e;
    }
}

const uploadContent = async (entry: FlightExpenseContent): Promise<void> => {
    const inFlight = uploadsInFlight.get(entry.key);
    if (inFlight) {
        return inFlight;
    }
    const upload = uploadPendingContent(entry).finally(() => uploadsInFlight.delete(entry.key));
    uploadsInFlight.set(entry.key, upload);
    return upload;
};

export const flightExpenseToDb: DbSyncerUpDefinition = (record: FlightExpenseRecord) => {
    const c = ++counter;
    const [commandName, inputType] = record._version
        ? ['updateFlightExpense', 'UpdateFlightExpenseInput']
        : ['createFlightExpense', 'CreateFlightExpenseInput'];
    const input = async (): Promise<CreateFlightExpenseInput> => {
        // Every file is in storage before the server learns its key. A deleted record uploads nothing.
        if (!record.deleted) {
            await Promise.all(record.contentKeys.map(uploadContent));
        }
        // noinspection UnnecessaryLocalVariableJS
        const input: CreateFlightExpenseInput = {
            id: record.id,
            deleted: record.deleted,
            createdAt: record.createdAt.toISOString(),
            aircraftId: record.aircraftId,
            flightId: record.flightId,
            legId: record.legId,
            phase: record.phase,
            bankTransactionId: record.bankTransactionId,
            contentKeys: record.contentKeys,
            // The converter lambda's work queue, sent as stored. It is a nullable index key: DynamoDB
            // rejects it written as null on a create, and an update clears it with an explicit null.
            mediaFilesNotCreated: record.mediaFilesNotCreated ?? (record._version ? null : undefined),
            title: record.title,
            caption: record.caption,
            documentType: record.documentType,
            // Set once at capture from the record's own createdAt in device-local time; syncing
            // must never recompute it, or another device in another timezone could move the row
            // between the accountant's months.
            expenseMonth: record.expenseMonth,
            status: record.status,
            name: record.name,
            amount: record.amount,
            currency: record.currency,
            paymentMethod: record.paymentMethod,
            paymentProvider: record.paymentProvider,
            reason: record.reason,
            reasonNote: record.reasonNote,
            includes: record.includes,
            noDocumentReason: record.noDocumentReason,
            noDocumentNote: record.noDocumentNote,
            _version: record._version,
        };
        return input;
    };

    const { mutationName, inputKey, query } = getStandardSyncerFields(commandName, c, flightExpenseItemFields);
    // A stale _version makes AUTOMERGE drop the write's scalars while its lists still merge in. Three
    // losses are repaired by keeping the local row as it must be and sending it again, now with the
    // returned version. A bank-linked expense that was soft-deleted elsewhere while still untouched
    // (the sweep removes the expense of an expired hold) stays deleted although the pilot put a
    // statement on it offline. And the marker is dropped, so the converter would never see the new
    // entries. And every other field the pilot owns is simply gone from the server and overwritten
    // on the device.
    const staysAlive = (returned: CreateFlightExpenseInput): boolean =>
        returned.deleted &&
        !record.deleted &&
        isBankLinked(record) &&
        (record.contentKeys.length > 0 || Boolean(record.noDocumentReason));
    const lostMarker = (returned: CreateFlightExpenseInput): boolean =>
        record.mediaFilesNotCreated === MEDIA_FILES_NOT_CREATED &&
        returned.mediaFilesNotCreated !== MEDIA_FILES_NOT_CREATED &&
        (!returned.deleted || staysAlive(returned));
    // A field the device never set is not an edit and takes the returned value, so a leg the sweep
    // found or a marker it cleared is never sent back. The bank owns amount, currency and payment
    // method of a linked expense; whether it is linked is the returned row's word, because an attach
    // links it on the server first.
    const droppedEdits = (returned: CreateFlightExpenseInput): Partial<FlightExpenseRecord> => {
        const server = flightExpenseTransform(returned);
        const pilotFields: Array<keyof FlightExpenseRecord> = [
            'title',
            'caption',
            'name',
            'reason',
            'reasonNote',
            'noDocumentReason',
            'noDocumentNote',
            'aircraftId',
            'flightId',
            'legId',
            ...(isBankLinked(server) ? [] : (['amount', 'currency', 'paymentMethod', 'paymentProvider'] as const)),
        ];
        const kept: Partial<FlightExpenseRecord> = {};
        for (const field of pilotFields) {
            if (record[field] !== undefined && (record[field] ?? null) !== (server[field] ?? null)) {
                Object.assign(kept, { [field]: record[field] });
            }
        }
        // Lists merge instead of being replaced, so an entry the pilot removed comes back.
        const sameEntries = (left: string[], right: string[]): boolean =>
            left.length === right.length && [...left].sort().join(',') === [...right].sort().join(',');
        if (record.includes !== undefined && !sameEntries(record.includes, server.includes ?? [])) {
            kept.includes = record.includes;
        }
        if (record.deleted && !server.deleted) {
            kept.deleted = true;
        }
        return kept;
    };
    const getNeedsResync = (returned: CreateFlightExpenseInput): boolean =>
        staysAlive(returned) || lostMarker(returned) || Object.keys(droppedEdits(returned)).length > 0;
    const remoteToLocalTransform = (returned: CreateFlightExpenseInput): FlightExpenseRecord => ({
        ...flightExpenseTransform(returned),
        ...droppedEdits(returned),
        ...(staysAlive(returned) ? { deleted: false } : {}),
        ...(lostMarker(returned) ? { mediaFilesNotCreated: MEDIA_FILES_NOT_CREATED } : {}),
    });
    return {
        query,
        input,
        inputKey,
        inputType,
        mutationName,
        remoteToLocalTransform,
        getNeedsResync,
        queryName: 'getFlightExpense',
    };
};

export const flightExpenseTransform = (input: CreateFlightExpenseInput): FlightExpenseRecord => ({
    id: input.id!,
    deleted: input.deleted,
    createdAt: new Date(input.createdAt!),
    aircraftId: input.aircraftId || undefined,
    flightId: input.flightId || undefined,
    legId: input.legId || undefined,
    phase: (input.phase as FlightLegPhase) || undefined,
    bankTransactionId: input.bankTransactionId || undefined,
    // AUTOMERGE merges lists on a stale _version, so an entry can come back twice.
    contentKeys: [
        ...new Map(
            (input.contentKeys ?? []).map(({ key, type }): [string, FlightExpenseContent] => [key, { key, type }])
        ).values(),
    ],
    mediaFilesNotCreated: input.mediaFilesNotCreated || undefined,
    title: input.title,
    caption: input.caption,
    documentType: (input.documentType as FlightDocumentType) || undefined,
    expenseMonth: input.expenseMonth || undefined,
    status: input.status || undefined,
    name: input.name || undefined,
    amount: input.amount ?? undefined,
    currency: input.currency || undefined,
    paymentMethod: input.paymentMethod || undefined,
    paymentProvider: input.paymentProvider || undefined,
    // AUTOMERGE merges lists on a stale _version, so an entry can come back twice.
    includes: input.includes ? [...new Set(input.includes)] : undefined,
    reason: input.reason || undefined,
    reasonNote: input.reasonNote,
    noDocumentReason: input.noDocumentReason || undefined,
    noDocumentNote: input.noDocumentNote,
    _version: input._version!,
    editableByUserIds: input.editableByUserIds || [],
});

export const flightExpenseItemHandler = async (items: Array<CreateFlightExpenseInput & DbCreated>): Promise<void> => {
    const db = getDb();
    // The converter and the bank sweep write to a pilot's rows too; the standard handler keeps rows whose
    // up-sync is still queued.
    await getStandardItemHandler(db.flightExpenses, flightExpenseTransform)(items);
    // After the deleted rows are gone, so a file another live expense still holds keeps its blob.
    await deleteUnheldExpenseContents(
        items.filter((i) => i.deleted).flatMap((i) => (i.contentKeys ?? []).map((entry) => entry.key))
    );
};
